import { Button, Card, CardBody, Form, Table } from "react-bootstrap";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { forwardRef, useEffect, useMemo, useState, useRef } from "react";
import useStore from "../store/payablePayment";
import CustomDatePicker from "../../../components/CustomDatePicker";
import swal from "sweetalert";
import useReOpenTicketTransaction from "../../../hooks/customHook/useReOpenTicketIdGenerator";
import BASE_URL from "../../../assets/global/url";
import axios from "axios";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { usePayablePDF, PayablePDFFrame } from "./PayablePDF2";
import {
  currencyList,
  currencySymbol,
  formatToNumber,
  formatToTwoDecimal,
  parseNumber,
} from "../../../utils/numberFormatter";

const defaultItems = {
  product_vendor_id: "",
  product_id: "",
  product_code: "",
  product_name: "",
  unit_of_measure: "",
  weight: "",
  moistureType: "%",
  moisture: 0,
  net_weight: 0,
  unitPrice: 0,
  supplier_price: 0,
  totalPrice: 0,
};

// For Deadline Date
const DateInput = forwardRef(({ value, onClick, disabled }, ref) => (
  <Form.Control
    value={value}
    onClick={onClick}
    disabled={disabled}
    readOnly
    ref={ref}
  />
));

const DateInputPurchase = forwardRef(({ value, onClick, disabled }, ref) => (
  <Form.Control
    value={value}
    onClick={onClick}
    disabled={disabled}
    readOnly
    ref={ref}
    style={{
      cursor: disabled ? "not-allowed" : "pointer",
      // --dm-* only exist in dark mode; light mode keeps white / black
      backgroundColor: disabled ? "" : "var(--dm-surface-2, white)",
      color: disabled ? "" : "var(--dm-text, black)",
      opacity: disabled ? "" : "1",
    }}
  />
));

const formatInputNumber = (e) => {
  let val = e.target.value.replace(/[^0-9.]/g, "");

  if (val === "") val = "0";
  if (val.startsWith(".")) val = "0" + val;

  const parts = val.split(".");
  if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");

  if (val.length > 1 && val.startsWith("0") && !val.startsWith("0.")) {
    val = val.replace(/^0+/, "");
    if (val === "") val = "0";
  }

  const numericVal = parseNumber(val);
  if (numericVal > 100000000) {
    swal({
      title: "Limit Exceeded",
      text: "The maximum allowed input is 100,000,000.",
      icon: "warning",
      button: "OK",
    });
    val = "100000000";
  }

  const [whole, fraction] = val.split(".");
  val =
    whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
    (fraction !== undefined ? "." + fraction : "");

  e.target.value = val;
  return e;
};

const getRowChangeStatus = (origItem, currentItem) => {
  const origWeight = parseNumber(origItem?.weight);
  const currWeight = parseNumber(currentItem?.weight);

  const origMoisture = parseNumber(origItem?.moisture);
  const currMoisture = parseNumber(currentItem?.moisture);

  const origNetWeight = parseNumber(origItem?.net_weight);
  const currNetWeight = parseNumber(currentItem?.net_weight);

  const origPrice = parseNumber(origItem?.unitPrice);
  const currPrice = parseNumber(currentItem?.unitPrice);

  const isWeightChanged = origWeight !== currWeight;
  const isMoistureChanged = origMoisture !== currMoisture;
  const isPriceChanged = origPrice !== currPrice;
  const isNetWeightChange = origNetWeight !== currNetWeight;

  const isChanged =
    isWeightChanged || isMoistureChanged || isPriceChanged || isNetWeightChange;

  const changedInputStyle = isChanged
    ? { color: "#d25100", borderColor: "#fd7e14" }
    : {};

  return {
    isWeightChanged,
    isMoistureChanged,
    isPriceChanged,
    isNetWeightChange,
    isChanged,
    changedInputStyle,
  };
};

const getResponsiveStyle = (charCount, changedStyle = {}) => ({
  width: "100%",
  minWidth: `${charCount}ch`,
  ...changedStyle,
});

const getColumnSizes = (items) => {
  const sizes = {
    product_code: 10,
    product_name: 10,
    unit_of_measure: 10,
    weight: 10,
    moisture: 10,
    net_weight: 10,
    unitPrice: 15,
    totalPrice: 15,
  };

  (items || []).forEach((item) => {
    Object.keys(sizes).forEach((key) => {
      const valLen = String(item[key] || "").length;
      const paddedLen = valLen + 2;
      if (paddedLen > sizes[key]) sizes[key] = paddedLen;
    });
  });

  return sizes;
};

// Dynamic Coloring for Global Summary
const getComparisonColor = (orig, updated) => {
  if (updated < orig) return "red";
  if (updated > orig) return "green";
  return "inherit";
};

const PayableReOpenTicket = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const userLoggedID = useDecodeToken();
  const isViewMode = location.state?.isViewMode || false;
  const purchaseReOpenDateRef = useRef(null);

  const {
    vendorId,
    vendorName,
    // email,
    // country,
    warehouseID,
    warehouseName,
    transaction_id,
    clientTransactionId,
    // createdAt,
    // isPaid,
    due_date,
    purchaseDate,
    containerNumber,
    pier,
    currencyId,
    currency_name,
    description,
    modeOfPayment,
    domestic_type,
    tracking_number,
    weighingFee,
    discountType,
    // approvedBy,
    // createdBy,
    // isCutoffPosted,
    discountValue,
    status,
    dataProduct,
    dataOriginalPayable,
    dataOtherFees,
    fetchData,
    // removeDataProduct,
    // removeDataProductIds,
    currencyRate,
    // updateDataProduct,
    vat_percent,
    tax_id,
    tax_rate,
    isSpecialTax,
  } = useStore();

  const [hasChanges, setHasChanges] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [print, setPrint] = useState(false);
  // Generated once each time the PDF modal opens, shared by viewer and download
  const payablePdf = usePayablePDF(id, print);
  const [taxSettings, setTaxSettings] = useState([]);
  const ROTransactionId = useReOpenTicketTransaction(isViewMode);
  const isEditable = !isViewMode || isEditMode;

  // Special Tax States
  const [originalIsSpecialTax, setOriginalIsSpecialTax] = useState(false);
  const [backupVat, setBackupVat] = useState("");
  const [backupTaxId, setBackupTaxId] = useState("");
  const [backupTaxRate, setBackupTaxRate] = useState(0);

  const fetchActivePurchaseTaxes = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/payable/getActivePurchaseTax`);
      setTaxSettings(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (id) fetchData(id);
    fetchActivePurchaseTaxes();
  }, [id, fetchData]);

  useEffect(() => {
    if (isViewMode && dataOriginalPayable) {
      setOriginalIsSpecialTax(dataOriginalPayable.isSpecialTax || false);
    } else {
      setOriginalIsSpecialTax(isSpecialTax || false);
    }
  }, [isSpecialTax, isViewMode, dataOriginalPayable]);

  const passedTransaction = location.state?.transactions ?? [];
  const passedVendor = location.state?.vendor ?? null;
  const passedCutOff = location.state?.selectedCutOff ?? {
    from: null,
    to: null,
  };

  const hasPreviousState =
    passedTransaction.length > 0 && passedVendor && passedCutOff;

  // Reusable mapper function
  const mapProductItem = (item) => {
    const rawNetWeight = parseNumber(item.net_weight);
    const rawUnitPrice = parseNumber(item.unitPrice);
    const rawTotalPrice = rawNetWeight * rawUnitPrice;
    return {
      ...item,
      product_code: item.product_tag_vendor?.product_list?.product_code || "",
      product_name: item.product_tag_vendor?.product_list?.product_name || "",
      unit_of_measure:
        item.product_tag_vendor?.product_list?.unit_of_measure || "",
      weight: formatToNumber(item.weight),
      moisture: formatToNumber(item.moisture),
      net_weight: formatToNumber(rawNetWeight),
      unitPrice: formatToTwoDecimal(rawUnitPrice),
      totalPrice: formatToTwoDecimal(rawTotalPrice),
    };
  };

  // Purely conditionally render Original vs New items based on isViewMode
  const mappedOriginalItems = useMemo(() => {
    const originalProducts = dataOriginalPayable?.payable_products;

    const source =
      originalProducts?.length > 0 ? originalProducts : dataProduct;

    if (source?.length > 0) return source.map(mapProductItem);

    return Array.from({ length: 5 }, () => ({ ...defaultItems }));
  }, [dataProduct, dataOriginalPayable]);

  const mappedNewItems = useMemo(() => {
    if (isViewMode && dataProduct?.length > 0) {
      return dataProduct.map(mapProductItem);
    }
    return mappedOriginalItems;
  }, [isViewMode, dataProduct, mappedOriginalItems]);

  const mappedOtherFees = useMemo(() => {
    if (dataOtherFees?.length > 0) {
      return dataOtherFees.map((fee) => ({
        feeName: fee.fee_name || fee.feeName || "",
        feeAmount: formatToTwoDecimal(fee.fee_amount || fee.feeAmount || 0),
      }));
    }
    return [];
  }, [dataOtherFees]);

  // Original Payable
  const origTransactionId = dataOriginalPayable?.transaction_id || "";
  const origPurchaseDate = dataOriginalPayable?.purchaseDate || "";

  const {
    control,
    setValue,
    getValues,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    values: {
      transactionId: isViewMode ? origTransactionId : transaction_id,
      rotId: isViewMode ? clientTransactionId : ROTransactionId,
      warehouse: warehouseID,
      vendor: vendorId,
      currency: currencyId,
      containerNumber: containerNumber,
      pier: pier,
      modeOfPayment: modeOfPayment,
      purchaseDate: isViewMode ? origPurchaseDate : purchaseDate,
      purchaseReOpenDate: purchaseDate,
      deadline: due_date,
      destination: domestic_type,
      vat: vat_percent,
      taxId: tax_id,
      taxRate: tax_rate,
      remarks: description,
      discountType: discountType,
      discountValue: discountValue,
      trackingNumber: tracking_number,
      weighingFee: weighingFee,
      itemOriginal: mappedOriginalItems,
      itemNew: mappedNewItems,
      otherFeesOriginal: mappedOtherFees,
      otherFeesNew: [],
      isSpecialTax: isSpecialTax,
    },
    resetOptions: { keepDirtyValues: true },
  });

  // useWatch values
  const [
    watchedOriginalItems,
    watchedNewItems,
    watchedOriginalFees,
    watchedNewFees,
    watchedVat,
    watchedTaxId,
    watchedTaxRate,
    watchedPurchaseReOpenDate,
    watchedIsSpecialTax,
  ] = useWatch({
    control,
    name: [
      "itemOriginal",
      "itemNew",
      "otherFeesOriginal",
      "otherFeesNew",
      "vat",
      "taxId",
      "taxRate",
      "purchaseReOpenDate",
      "isSpecialTax",
    ],
  });

  const { fields: itemList } = useFieldArray({ control, name: "itemNew" });
  const {
    fields: otherFeeFields,
    append: appendOtherFee,
    remove: removeOtherFee,
  } = useFieldArray({ control, name: "otherFeesNew" });

  // Helper to dynamically calculate row values based on which field was edited
  const updateRowCalculations = (index, fieldChanged, newValue) => {
    const currentItem = getValues(`itemNew.${index}`);

    let weight = parseNumber(
      fieldChanged === "weight" ? newValue : currentItem?.weight || 0,
    );
    let moisture = parseNumber(
      fieldChanged === "moisture" ? newValue : currentItem?.moisture || 0,
    );
    let netWeight = parseNumber(
      fieldChanged === "net_weight" ? newValue : currentItem?.net_weight || 0,
    );
    let unitPrice = parseNumber(
      fieldChanged === "unitPrice" ? newValue : currentItem?.unitPrice || 0,
    );

    if (fieldChanged === "weight" || fieldChanged === "moisture") {
      // Calculate Net Weight based on Moisture
      netWeight = Math.trunc(weight * (1 - moisture / 100));
      setValue(`itemNew.${index}.net_weight`, formatToNumber(netWeight), {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else if (fieldChanged === "net_weight") {
      // Validate Net Weight
      if (netWeight > weight) {
        swal({
          title: "Limit exceeded",
          text: "Net Quantity cannot exceed the quantity.",
          icon: "warning",
          button: "OK",
        });
        netWeight = weight;
        setValue(`itemNew.${index}.net_weight`, formatToNumber(netWeight), {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      // Calculate Moisture based on Net Weight
      moisture = weight === 0 ? 0 : ((weight - netWeight) / weight) * 100;
      if (moisture < 0) moisture = 0;

      setValue(`itemNew.${index}.moisture`, formatToTwoDecimal(moisture), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    // Always recalculate Subtotal (Total Price)
    const totalPrice = netWeight * unitPrice;
    setValue(`itemNew.${index}.totalPrice`, formatToTwoDecimal(totalPrice), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // OnChange Handlers
  const handleWeightChange = (e, index) => {
    formatInputNumber(e);
    updateRowCalculations(index, "weight", e.target.value);
    setHasChanges(true);
  };

  const handleMoistureChange = (e, index) => {
    formatInputNumber(e);
    let val = parseNumber(e.target.value);

    if (val > 100) {
      swal({
        title: "Limit exceeded",
        text: "Moisture cannot exceed 100%.",
        icon: "warning",
        button: "OK",
      });
      val = 100;
      e.target.value = "100";
      setValue(`itemNew.${index}.moisture`, "100", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    updateRowCalculations(index, "moisture", val);
    setHasChanges(true);
  };

  const handleNetWeightChange = (e, index) => {
    formatInputNumber(e);
    updateRowCalculations(index, "net_weight", e.target.value);
    setHasChanges(true);
  };

  const handleUnitPriceChange = (e, index) => {
    formatInputNumber(e);
    updateRowCalculations(index, "unitPrice", e.target.value);
    setHasChanges(true);
  };

  // Global Summary
  const summary = useMemo(() => {
    const calcTotals = (items) => {
      return (items || []).reduce(
        (acc, item) => {
          const w = parseNumber(item.weight);
          const nw = parseNumber(item.net_weight);
          const up = parseNumber(item.unitPrice);
          const tp = parseNumber(item.totalPrice) || nw * up;
          const moistureQty = w - nw;

          // Track Physical Quantities
          acc.weight += w;
          acc.moistureQty += moistureQty;
          acc.netWeight += nw;

          // Track Monetary Values (Prices)
          acc.qtyAmount += w * up;
          acc.moistureAmount += moistureQty * up;
          acc.netQtyAmount += tp;

          return acc;
        },
        {
          weight: 0,
          moistureQty: 0,
          netWeight: 0,
          qtyAmount: 0,
          moistureAmount: 0,
          netQtyAmount: 0,
        },
      );
    };

    // Calculation Flow: orig and update are isolated. Calculate the Subtotal then subtract with the discount, last is apply the VAT
    const orig = calcTotals(watchedOriginalItems);
    const updated = calcTotals(watchedNewItems);

    const uom = "Kg";
    const symbol = currencySymbol[currency_name];
    const wFee = parseNumber(weighingFee);

    // Calculate Total Other Fees
    const origOtherFeesTotal = (watchedOriginalFees || []).reduce(
      (acc, fee) => acc + parseNumber(fee.feeAmount),
      0,
    );

    // Sum up ONLY the newly added fees
    const newlyAddedFeesTotal = (watchedNewFees || []).reduce(
      (acc, fee) => acc + parseNumber(fee.feeAmount),
      0,
    );

    // The updated total fees is the Original Fees + Any New Fees
    const updatedOtherFeesTotal = origOtherFeesTotal + newlyAddedFeesTotal;

    const origProductSubtotal = orig.netQtyAmount;
    const updatedProductSubtotal = updated.netQtyAmount;

    // ─── Subtotal display row (same as product subtotal) ──────────────────────
    const origSubtotal = origProductSubtotal;
    const updatedSubtotal = updatedProductSubtotal;

    // ─── Discount — always %, basis is product subtotal only ──────────────────
    const dVal = parseNumber(discountValue);
    const origDiscountAmount = origProductSubtotal * (dVal / 100);
    const updatedDiscountAmount = updatedProductSubtotal * (dVal / 100);

    // ─── Gross Amount ──────────────────────────────────────────────────────────
    const origGrossAmount = origProductSubtotal - origDiscountAmount;
    const updatedGrossAmount = updatedProductSubtotal - updatedDiscountAmount;

    // ─── VAT — basis is grossAmount (products only) ────────────────────────────
    const vRate = parseNumber(watchedVat) / 100;
    const origVatAmount = originalIsSpecialTax ? 0 : origGrossAmount * vRate;
    const updatedVatAmount = watchedIsSpecialTax
      ? 0
      : updatedGrossAmount * vRate;

    // ─── Withholding Tax — basis is grossAmount (products only) ───────────────
    const tRate = parseNumber(watchedTaxRate) / 100;
    const origTaxAmount = originalIsSpecialTax ? 0 : origGrossAmount * tRate;
    const updatedTaxAmount = watchedIsSpecialTax
      ? 0
      : updatedGrossAmount * tRate;

    const origSpecialTax = originalIsSpecialTax ? origGrossAmount * 0.1088 : 0;
    const updatedSpecialTax = watchedIsSpecialTax
      ? updatedGrossAmount * 0.1088
      : 0;

    const origFinalTotal =
      origGrossAmount +
      origVatAmount -
      origTaxAmount +
      origSpecialTax +
      wFee +
      origOtherFeesTotal;

    const updatedFinalTotal =
      updatedGrossAmount +
      updatedVatAmount -
      updatedTaxAmount +
      updatedSpecialTax +
      wFee +
      updatedOtherFeesTotal;

    const difference = updatedFinalTotal - origFinalTotal;

    return {
      orig,
      updated,
      uom,
      symbol,
      wFee,
      origOtherFeesTotal,
      updatedOtherFeesTotal,
      origSubtotal,
      updatedSubtotal,
      origDiscountAmount,
      updatedDiscountAmount,
      origGrossAmount,
      updatedGrossAmount,
      origVatAmount,
      updatedVatAmount,
      origTaxAmount,
      updatedTaxAmount,
      origSpecialTax,
      updatedSpecialTax,
      origFinalTotal,
      updatedFinalTotal,
      difference,
    };
  }, [
    watchedOriginalItems,
    watchedNewItems,
    watchedOriginalFees,
    watchedNewFees,
    watchedVat,
    watchedTaxRate,
    watchedIsSpecialTax,
    originalIsSpecialTax,
    currency_name,
    weighingFee,
    discountValue,
  ]);

  const maxOriginalTableInputSizes = useMemo(
    () => getColumnSizes(watchedOriginalItems),
    [watchedOriginalItems],
  );
  const maxNewTableInputSizes = useMemo(
    () => getColumnSizes(watchedNewItems),
    [watchedNewItems],
  );

  const handleApprove = () => {
    swal({
      title: "Approve this Re-Open Ticket?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (approve) => {
      if (approve) {
        const productData = dataProduct.map((data) => ({
          product_id: data.product_tag_vendor.product_id,
          costing: data.unitPrice,
          weight: data.weight,
          moistureType: data.moisture_type,
          netWeight:
            data.moisture_type === "%"
              ? Math.trunc(data.weight * (1 - data.moisture / 100))
              : Math.trunc(data.weight - data.moisture),
        }));

        const { totalQuantity, averageUnitPrice } = (() => {
          const totalQuantity = productData.reduce(
            (acc, value) => acc + value.netWeight,
            0,
          );

          const { totalCount, totalUnitPrice } = productData.reduce(
            (acc, value) => {
              acc.totalCount += 1;
              acc.totalUnitPrice += value.costing;
              return acc;
            },
            { totalCount: 0, totalUnitPrice: 0 },
          );

          const averageUnitPrice =
            totalCount > 0 ? (totalUnitPrice / totalCount).toFixed(2) : "0.00";

          return { totalQuantity, averageUnitPrice };
        })();

        let paymentType;
        let totalAmount;

        if (summary.difference < 0) {
          paymentType = "Credit";
          totalAmount = Math.abs(summary.difference).toFixed(2);
        } else {
          paymentType = "Debit";
          totalAmount = summary.difference.toFixed(2);
        }

        try {
          const res = await axios.put(
            `${BASE_URL}/payable/reOpenApproval/${id}`,
            {
              status: "Approved",
              products: productData,
              vendorId: vendorId,
              purchaseDate: purchaseDate,
              transaction_id: isViewMode
                ? clientTransactionId
                : ROTransactionId,
              approved_by: userLoggedID,
              warehouseID: warehouseID,
              currencyRate: currencyRate,
              currencyName: currency_name ?? "PHP",
              payableJournal: {
                totalAmount,
                totalQuantity,
                averageUnitPrice,
                paymentType,
              },
            },
          );

          if (res.status === 200) {
            swal({
              title: "Approved!",
              text: "The Re-Open Ticket has been approved.",
              icon: "success",
              button: "OK",
            }).then(() => {
              navigate(
                hasPreviousState
                  ? `/purchases/transaction-list`
                  : `/purchases/payable`,
                hasPreviousState
                  ? {
                      state: {
                        transactions: passedTransaction,
                        vendor: passedVendor,
                        selectedCutOff: passedCutOff,
                      },
                    }
                  : undefined,
              );
            });
          }
        } catch (error) {
          console.error("Approved Error:", error);
          swal("Error", "Something went wrong.", "error");
        }
      }
    });
  };

  const handleReject = () => {
    swal({
      title: "Reject this Re-Open Ticket?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (reject) => {
      if (reject) {
        try {
          const res = await axios.put(
            `${BASE_URL}/payable/reOpenApproval/${id}`,
            { status: "Rejected", approved_by: userLoggedID },
          );
          if (res.status === 200) {
            swal({
              title: "Rejected!",
              text: "The Re-Open Ticket has been rejected.",
              icon: "success",
              button: "OK",
            }).then(() => {
              navigate(
                hasPreviousState
                  ? `/purchases/transaction-list`
                  : `/purchases/payable`,
                hasPreviousState
                  ? {
                      state: {
                        transactions: passedTransaction,
                        vendor: passedVendor,
                        selectedCutOff: passedCutOff,
                      },
                    }
                  : undefined,
              );
            });
          }
        } catch (error) {
          console.error(error);
          swal("Error", "Something went wrong.", "error");
        }
      }
    });
  };

  const validateAndFocusReOpenDate = (data) => {
    const focusField = () => {
      if (purchaseReOpenDateRef.current) {
        purchaseReOpenDateRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        const input = purchaseReOpenDateRef.current.querySelector("input");
        if (input) {
          input.focus();
          input.style.borderColor = "#dc3545";
          input.style.boxShadow = "0 0 0 0.2rem rgba(220,53,69,.25)";
          input.addEventListener(
            "click",
            () => {
              input.style.borderColor = "";
              input.style.boxShadow = "";
            },
            { once: true },
          );
        }
      }
    };

    // Check 1: empty
    if (!data.purchaseReOpenDate) {
      swal({
        title: "Required Field Missing",
        text: "Please fill in the Purchase Re-Open Date before saving.",
        icon: "error",
        button: "OK",
      }).then(() => focusField());
      return false;
    }

    // Check 2: month of Re-Open Date must be GREATER than month of Purchase Date
    if (data.purchaseDate) {
      const reOpenDate = new Date(data.purchaseReOpenDate);
      const origPurchaseDate = new Date(data.purchaseDate);

      // Strip to year+month only for comparison
      const reOpenYearMonth =
        reOpenDate.getFullYear() * 12 + reOpenDate.getMonth();
      const purchaseYearMonth =
        origPurchaseDate.getFullYear() * 12 + origPurchaseDate.getMonth();

      const purchaseMonthLabel = origPurchaseDate.toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      });

      if (reOpenYearMonth <= purchaseYearMonth) {
        swal({
          title: "Invalid Purchase Re-Open Date",
          text: `Purchase Re-Open Date must be a month after the Purchase Date (${purchaseMonthLabel}).`,
          icon: "error",
          button: "OK",
        }).then(() => focusField());
        return false;
      }
    }

    return true;
  };

  const onSubmit = async (data) => {
    if (!validateAndFocusReOpenDate(data)) return;

    if (isViewMode && isEditMode) {
      return handleUpdate(data);
    }

    if (summary.updatedFinalTotal < 0)
      return swal({
        title: "New Total Amount is negative",
        text: "New Total Amount must be greater than 0.",
        icon: "error",
        button: "OK",
      });

    if (summary.difference === 0)
      return swal({
        title: "There are no changes",
        text: "Make changes to create a new Re-Open Ticket.",
        icon: "error",
        button: "OK",
      });

    const confirmed = await swal({
      title: "Create this new Re-Open Ticket?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (confirmed) {
      try {
        const payloadItems = data.itemNew.map((item) => ({
          product_vendor_id: item.product_vendor_id,
          moisture: parseNumber(item.moisture),
          moistureType: "%",
          weight: parseNumber(item.weight),
          unitPrice: parseNumber(item.unitPrice),
          net_weight: parseNumber(item.net_weight),
        }));

        const combinedOtherFees = [
          ...(watchedOriginalFees || []),
          ...(data.otherFeesNew || []),
        ].map((fee) => ({
          feeName: fee.feeName,
          feeAmount: parseNumber(fee.feeAmount),
        }));

        const payload = {
          id,
          transactionId: data.transactionId,
          clientTransactionId: data.rotId,
          warehouseId: data.warehouse,
          vendorId: data.vendor,
          modeOfPayment: data.modeOfPayment,
          customerType: data.destination,
          trackingNumber: data.trackingNumber || "",
          dueDate: data.deadline,
          weighingFee: parseNumber(weighingFee),
          items: payloadItems,
          otherFee: JSON.stringify(combinedOtherFees),
          primary_discount: parseNumber(data.discountValue),
          isPercent: data.discountType === "%" ? 1 : 0,
          calculatedAmount: summary.difference.toFixed(2),
          purchaseDate: data.purchaseDate,
          purchaseReOpenDate: data.purchaseReOpenDate,
          totalAmount: summary.difference.toFixed(2),
          description: data.remarks || "",
          currencyId: data.currency,
          createdBy: userLoggedID,
          containerNumber: data.containerNumber || "",
          pier: data.pier || "",
          currencyRate: currencyRate || 1,
          isReOpenAdded: true,
          vat: parseNumber(data.vat),
          tax_id: data.taxId || null,
          tax_rate: parseNumber(data.taxRate),
          isSpecialTax: data.isSpecialTax,
        };

        const res = await axios.post(
          `${BASE_URL}/payable/reOpenCreate`,
          payload,
        );

        if (res.status === 200) {
          swal({
            title: "Success",
            text: "Re-Open Ticket created successfully",
            icon: "success",
            timer: 2000,
            buttons: false,
          }).then(() => {
            navigate(`/purchases/transaction-list`, {
              state: {
                transactions: passedTransaction,
                vendor: passedVendor,
                selectedCutOff: passedCutOff,
              },
            });
          });
        }
      } catch (error) {
        if (error.response?.status === 409) {
          swal({
            icon: "error",
            title: "Transaction ID Conflict",
            text: "The transaction ID already exists.",
          });
        } else {
          console.error(error);
          swal({
            icon: "error",
            title: "Error",
            text: "Something went wrong creating the ticket.",
          });
        }
      }
    }
  };

  const handleUpdate = async (data) => {
    if (!validateAndFocusReOpenDate(data)) return;
    const confirmed = await swal({
      title: "Update this Re-Open Ticket?",
      text: "Action cannot be undone!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmed) return;

    try {
      const payloadItems = data.itemNew.map((item) => ({
        id: item.id || null,
        product_vendor_id: item.product_vendor_id,
        moisture: parseNumber(item.moisture),
        moistureType: item.moistureType || "%",
        weight: parseNumber(item.weight),
        unitPrice: parseNumber(item.unitPrice),
        net_weight: parseNumber(item.net_weight),
      }));

      const combinedOtherFees = [
        ...(watchedOriginalFees || []),
        ...(data.otherFeesNew || []),
      ].map((fee) => ({
        feeName: fee.feeName,
        feeAmount: parseNumber(fee.feeAmount),
      }));

      const payload = {
        purchaseDate: data.purchaseDate,
        purchaseReOpenDate: data.purchaseReOpenDate,
        vat: parseNumber(data.vat),
        tax_id: data.taxId || null,
        tax_rate: parseNumber(data.taxRate),
        isSpecialTax: data.isSpecialTax,
        items: payloadItems,
        otherFee: JSON.stringify(combinedOtherFees),
        totalAmount: summary.difference.toFixed(2),
        re_open_amount: summary.difference.toFixed(2),
        reopenPayableId: id,
        updatedBy: userLoggedID,
      };

      const res = await axios.put(
        `${BASE_URL}/payable/reOpenUpdate/${id}`,
        payload,
      );

      if (res.status === 200) {
        swal({
          title: "Success",
          text: "Re-Open Ticket updated successfully",
          icon: "success",
          timer: 2000,
          buttons: false,
        }).then(() => {
          setIsEditMode(false);
          setHasChanges(false);
          fetchData(id);
        });
      }
    } catch (error) {
      console.error(error);
      swal({
        icon: "error",
        title: "Error",
        text: "Something went wrong updating the ticket.",
      });
    }
  };

  return (
    <div className="h-100 w-100 custom-container bg-white">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3">
            <Link
              to={
                hasPreviousState
                  ? `/purchases/transaction-list`
                  : `/purchases/payable`
              }
              state={
                hasPreviousState
                  ? {
                      transactions: passedTransaction,
                      vendor: passedVendor,
                      selectedCutOff: passedCutOff,
                    }
                  : undefined
              }
              className="text-dark mx-2"
            >
              <i className="fa-solid fa-arrow-left"></i>
            </Link>
            RE-OPEN TICKET DETAILS
          </span>
        </div>
        {/* Preview PDF */}
        <Button
          variant="outline-danger"
          type="button"
          onClick={() => setPrint(true)}
        >
          Preview PDF
        </Button>
      </div>

      <Form
        id="re-open-ticket-form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        {/* Top Card - Vendor/Warehouse/Email/Country row */}
        <div className="border p-3 shadow-sm rounded mb-0">
          <div className="row mx-auto p-3">
            <div className="col-sm d-flex flex-column mb-2">
              <h6>Supplier</h6>
              <span className="text-secondary">{vendorName}</span>
            </div>
            <div className="col-sm d-flex flex-column mb-2">
              <h6>Mode of Payment</h6>
              <span className="text-secondary">{modeOfPayment}</span>
            </div>
            <div className="col-sm d-flex flex-column mb-2">
              <h6>Receiving Warehouse</h6>
              <span className="text-secondary">{warehouseName}</span>
            </div>
          </div>
        </div>

        {/* Second Card - Transaction details */}
        <div className="border-start border-bottom border-end p-3 shadow-sm rounded mb-3">
          <div className="row mx-auto p-3">
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">Re-Open Transaction ID</h6>
              <h5>
                <Form.Control
                  type="text"
                  {...register("rotId")}
                  readOnly
                  className="pointer-events-none"
                />
              </h5>
            </div>
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">Original Transaction ID</h6>
              <h5>
                <Form.Control
                  type="text"
                  {...register("transactionId")}
                  readOnly
                  className="pointer-events-none"
                />
              </h5>
            </div>
          </div>

          <div className="row mx-auto p-2">
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">
                Purchase Re-Open Date <span className="text-danger">*</span>
              </h6>
              <div ref={purchaseReOpenDateRef}>
                <Controller
                  control={control}
                  name="purchaseReOpenDate"
                  render={({ field: { value, onChange } }) => (
                    <CustomDatePicker
                      selected={value ? new Date(value) : null}
                      handleDateChange={(date) => {
                        onChange(date);
                        setHasChanges(true);
                      }}
                      disabled={!isEditable}
                      CustomInput={DateInputPurchase}
                    />
                  )}
                />
              </div>
            </div>
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">Purchase Date</h6>
              <Controller
                control={control}
                name="purchaseDate"
                render={({ field: { value } }) => (
                  <CustomDatePicker
                    selected={value ? new Date(value) : null}
                    disabled={true}
                    CustomInput={DateInputPurchase}
                  />
                )}
              />
            </div>
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">Deadline</h6>
              <Controller
                control={control}
                name="deadline"
                render={({ field: { value } }) => (
                  <CustomDatePicker
                    selected={value ? new Date(value) : null}
                    disabled={true}
                    CustomInput={DateInput}
                  />
                )}
              />
            </div>
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">Currency</h6>
              <h5>
                {currency_name} - {currencyList[currency_name]}
              </h5>
            </div>
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">Foreign Type</h6>
              <h5>{domestic_type?.toUpperCase()}</h5>
            </div>
          </div>

          <div className="row mx-auto p-2 mb-2">
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">VAT (%)</h6>
              {!isEditable ? (
                <h5>
                  {parseNumber(watchedVat) > 0 && !watchedIsSpecialTax
                    ? `VAT (${parseNumber(watchedVat)}%)`
                    : "Non-VAT"}
                </h5>
              ) : (
                <Form.Control
                  type="text"
                  id="vat"
                  placeholder="0.00"
                  maxLength={6}
                  disabled={watchedIsSpecialTax}
                  {...register("vat", {
                    onChange: (e) => {
                      let value = e.target.value;
                      value = value.replace(/[^0-9.]/g, "");
                      const parts = value.split(".");
                      if (parts.length > 2)
                        value = parts[0] + "." + parts.slice(1).join("");
                      if (parts[1]?.length > 2)
                        value = parts[0] + "." + parts[1].slice(0, 2);
                      if (parseNumber(value) > 100) value = "100";
                      setValue("vat", value);
                      setHasChanges(true);
                    },
                  })}
                />
              )}
            </div>
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">Withholding Tax</h6>
              {!isEditable ? (
                <h5>
                  {parseNumber(watchedTaxRate) > 0 && !watchedIsSpecialTax
                    ? `${parseNumber(watchedTaxRate)}%`
                    : "None"}
                </h5>
              ) : (
                <Form.Select
                  disabled={watchedIsSpecialTax}
                  {...register("taxId", {
                    onChange: (e) => {
                      const selected = taxSettings.find(
                        (t) => String(t.id) === String(e.target.value),
                      );
                      setValue("taxRate", selected ? selected.rate : 0);
                      setHasChanges(true);
                    },
                  })}
                >
                  <option value="">None</option>
                  {taxSettings.map((tax) => (
                    <option key={tax.id} value={tax.id}>
                      {tax.name} - {tax.rate}%
                    </option>
                  ))}
                </Form.Select>
              )}
            </div>
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">Container Number</h6>
              <h5>{containerNumber || "—"}</h5>
            </div>
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">Pier</h6>
              <h5>{pier || "—"}</h5>
            </div>
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">Weighing Fee</h6>
              <h5>
                {currencySymbol[currency_name]}{" "}
                {formatToTwoDecimal(weighingFee)}
              </h5>
            </div>
            {domestic_type !== "local" && (
              <div className="col-sm d-flex flex-column mb-2">
                <h6 className="text-secondary">Tracking Number</h6>
                <h5>{tracking_number || "—"}</h5>
              </div>
            )}
            <div className="col-sm d-flex flex-column mb-2">
              <h6 className="text-secondary">Remarks</h6>
              <h5>{description || "—"}</h5>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="w-100 my-3 d-flex align-items-center">
            <span className="fs-5">Original Item List</span>
            <hr className="flex-grow-1 mx-3" />
          </div>
          <Table bordered hover responsive>
            <thead>
              <tr>
                <th>Product Code</th>
                <th>Product Name</th>
                <th>Unit of Measure</th>
                <th>Qty</th>
                <th>Moisture</th>
                <th>Net Qty</th>
                <th>Unit Price</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {itemList.map((item, index) => (
                <tr key={item.id}>
                  <td>
                    <Form.Control
                      type="hidden"
                      {...register(`itemOriginal.${index}.product_id`)}
                      readOnly
                    />
                    <Form.Control
                      type="text"
                      {...register(`itemOriginal.${index}.product_code`)}
                      className="pointer-events-none"
                      style={getResponsiveStyle(
                        maxOriginalTableInputSizes.product_code,
                      )}
                      readOnly
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      {...register(`itemOriginal.${index}.product_name`)}
                      className="pointer-events-none"
                      style={getResponsiveStyle(
                        maxOriginalTableInputSizes.product_name,
                      )}
                      readOnly
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      {...register(`itemOriginal.${index}.unit_of_measure`)}
                      className="pointer-events-none"
                      style={getResponsiveStyle(
                        maxOriginalTableInputSizes.unit_of_measure,
                      )}
                      readOnly
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      {...register(`itemOriginal.${index}.weight`)}
                      className="pointer-events-none"
                      style={getResponsiveStyle(
                        maxOriginalTableInputSizes.weight,
                      )}
                      readOnly
                    />
                  </td>
                  <td>
                    <div
                      className="input-group flex-nowrap"
                      style={getResponsiveStyle(
                        maxOriginalTableInputSizes.moisture,
                      )}
                    >
                      <Form.Control
                        type="text"
                        {...register(`itemOriginal.${index}.moisture`)}
                        className="pointer-events-none"
                        style={getResponsiveStyle(
                          maxOriginalTableInputSizes.moisture,
                        )}
                        readOnly
                      />
                      <span className="input-group-text px-2">%</span>
                    </div>
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      {...register(`itemOriginal.${index}.net_weight`)}
                      className="pointer-events-none"
                      style={getResponsiveStyle(
                        maxOriginalTableInputSizes.net_weight,
                      )}
                      readOnly
                    />
                  </td>
                  <td>
                    <div
                      className="input-group flex-nowrap"
                      style={getResponsiveStyle(
                        maxOriginalTableInputSizes.unitPrice,
                      )}
                    >
                      <span className="input-group-text px-2">
                        {summary.symbol}
                      </span>
                      <Form.Control
                        type="text"
                        {...register(`itemOriginal.${index}.unitPrice`)}
                        className="pointer-events-none"
                        readOnly
                      />
                    </div>
                  </td>
                  <td>
                    <div
                      className="input-group flex-nowrap"
                      style={getResponsiveStyle(
                        maxOriginalTableInputSizes.totalPrice,
                      )}
                    >
                      <span className="input-group-text px-2">
                        {summary.symbol}
                      </span>
                      <Form.Control
                        type="text"
                        {...register(`itemOriginal.${index}.totalPrice`)}
                        className="pointer-events-none"
                        readOnly
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <div className="row">
          <div className="w-100 my-3 d-flex align-items-center">
            <span className="fs-5">New Changes</span>
            <hr className="flex-grow-1 mx-3" />
          </div>
          <Table bordered hover responsive>
            <thead>
              <tr>
                <th>Product Code</th>
                <th>Product Name</th>
                <th>Unit of Measure</th>
                <th>Qty</th>
                <th>Moisture</th>
                <th>Net Qty</th>
                <th>Unit Price</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {itemList.map((item, index) => {
                const orig = watchedOriginalItems?.[index] || {};
                const current = watchedNewItems?.[index] || {};

                const {
                  isWeightChanged,
                  isMoistureChanged,
                  isPriceChanged,
                  isNetWeightChange,
                  isChanged,
                  changedInputStyle,
                } = getRowChangeStatus(orig, current);

                return (
                  <tr
                    key={item.id}
                    className={isChanged ? "table-warning" : ""}
                  >
                    <td>
                      <Form.Control
                        type="hidden"
                        {...register(`itemNew.${index}.product_id`)}
                        readOnly
                      />
                      <Form.Control
                        type="text"
                        {...register(`itemNew.${index}.product_code`)}
                        className="pointer-events-none"
                        style={getResponsiveStyle(
                          maxNewTableInputSizes.product_code,
                        )}
                        readOnly
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`itemNew.${index}.product_name`)}
                        className="pointer-events-none"
                        style={getResponsiveStyle(
                          maxNewTableInputSizes.product_name,
                        )}
                        readOnly
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`itemNew.${index}.unit_of_measure`)}
                        className="pointer-events-none"
                        style={getResponsiveStyle(
                          maxNewTableInputSizes.unit_of_measure,
                        )}
                        readOnly
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`itemNew.${index}.weight`, {
                          onChange: (e) => handleWeightChange(e, index),
                        })}
                        className={`${isWeightChanged ? "fw-bold" : ""}`}
                        style={getResponsiveStyle(
                          maxNewTableInputSizes.weight,
                          changedInputStyle,
                        )}
                        readOnly={!isEditable}
                      />
                    </td>
                    <td>
                      <div
                        className="input-group flex-nowrap"
                        style={getResponsiveStyle(
                          maxNewTableInputSizes.moisture,
                          changedInputStyle,
                        )}
                      >
                        <Form.Control
                          type="text"
                          {...register(`itemNew.${index}.moisture`, {
                            onChange: (e) => handleMoistureChange(e, index),
                          })}
                          className={`${isMoistureChanged ? "fw-bold" : ""}`}
                          style={changedInputStyle}
                          readOnly={!isEditable}
                        />
                        <span
                          className="input-group-text px-2"
                          style={changedInputStyle}
                        >
                          %
                        </span>
                      </div>
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`itemNew.${index}.net_weight`, {
                          onChange: (e) => handleNetWeightChange(e, index),
                        })}
                        className={`${isNetWeightChange ? "fw-bold" : ""}`}
                        style={getResponsiveStyle(
                          maxNewTableInputSizes.net_weight,
                          changedInputStyle,
                        )}
                        readOnly={!isEditable}
                      />
                    </td>
                    <td>
                      <div
                        className="input-group flex-nowrap"
                        style={getResponsiveStyle(
                          maxNewTableInputSizes.unitPrice,
                          changedInputStyle,
                        )}
                      >
                        <span
                          className="input-group-text px-2"
                          style={changedInputStyle}
                        >
                          {summary.symbol}
                        </span>
                        <Form.Control
                          type="text"
                          {...register(`itemNew.${index}.unitPrice`, {
                            onChange: (e) => handleUnitPriceChange(e, index),
                          })}
                          className={`${isPriceChanged ? "fw-bold" : ""}`}
                          style={changedInputStyle}
                          readOnly={!isEditable}
                        />
                      </div>
                    </td>
                    <td>
                      <div
                        className="input-group flex-nowrap"
                        style={getResponsiveStyle(
                          maxNewTableInputSizes.totalPrice,
                          changedInputStyle,
                        )}
                      >
                        <span
                          className="input-group-text px-2"
                          style={changedInputStyle}
                        >
                          {summary.symbol}
                        </span>
                        <Form.Control
                          type="text"
                          {...register(`itemNew.${index}.totalPrice`)}
                          className="pointer-events-none"
                          style={changedInputStyle}
                          readOnly
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>

        <div className="row mt-4">
          <div className="col-sm">
            {/* Summary Weighted */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>Weighted</span>
              <div className="d-flex align-items-center">
                <span className="text-end" style={{ width: "150px" }}>
                  {formatToTwoDecimal(summary.orig.weight)}{" "}
                  ({summary.uom})
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start"
                  style={{
                    width: "150px",
                    color: getComparisonColor(
                      summary.orig.weight,
                      summary.updated.weight,
                    ),
                  }}
                >
                  {formatToTwoDecimal(summary.updated.weight)}{" "}
                  ({summary.uom})
                </span>
              </div>
            </div>

            {/* Summary Moisture */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>Moisture</span>
              <div className="d-flex align-items-center">
                <span className="text-end" style={{ width: "150px" }}>
                  {formatToTwoDecimal(summary.orig.moistureQty)}{" "}
                  ({summary.uom})
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start"
                  style={{
                    width: "150px",
                    color: getComparisonColor(
                      summary.orig.moistureQty,
                      summary.updated.moistureQty,
                    ),
                  }}
                >
                  {formatToTwoDecimal(summary.updated.moistureQty)}{" "}
                  ({summary.uom})
                </span>
              </div>
            </div>

            {/* Summary Net Weighted */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>Net Weighted</span>
              <div className="d-flex align-items-center">
                <span className="text-end" style={{ width: "150px" }}>
                  {formatToTwoDecimal(summary.orig.netWeight)}{" "}
                  ({summary.uom})
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start"
                  style={{
                    width: "150px",
                    color: getComparisonColor(
                      summary.orig.netWeight,
                      summary.updated.netWeight,
                    ),
                  }}
                >
                  {formatToTwoDecimal(summary.updated.netWeight)}{" "}
                  ({summary.uom})
                </span>
              </div>
            </div>
          </div>

          <div className="col-sm">
            {/* Total Qty Amount */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>Total Qty Amount</span>
              <div className="d-flex align-items-center">
                <span className="text-end" style={{ width: "150px" }}>
                  {summary.symbol}
                  {formatToTwoDecimal(summary.orig.qtyAmount)}
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start"
                  style={{
                    width: "150px",
                    color: getComparisonColor(
                      summary.orig.qtyAmount,
                      summary.updated.qtyAmount,
                    ),
                  }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.updated.qtyAmount)}
                </span>
              </div>
            </div>

            {/* Total Amount of Moisture */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>Total Amount of Moisture</span>
              <div className="d-flex align-items-center">
                <span
                  className="text-end text-danger"
                  style={{ width: "150px" }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.orig.moistureAmount)}
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start text-danger"
                  style={{ width: "150px" }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.updated.moistureAmount)}
                </span>
              </div>
            </div>

            {/* Total Net Qty Amount */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>Total Net Qty Amount</span>
              <div className="d-flex align-items-center">
                <span
                  className="text-end text-danger"
                  style={{ width: "150px" }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.orig.netQtyAmount)}
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start text-danger"
                  style={{ width: "150px" }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.updated.netQtyAmount)}
                </span>
              </div>
            </div>

            <hr className="flex-grow-1 mx-3" />

            {/* Subtotal */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>Subtotal</span>
              <div className="d-flex align-items-center">
                <span className="text-end" style={{ width: "150px" }}>
                  {summary.symbol}
                  {formatToTwoDecimal(summary.origSubtotal)}
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start"
                  style={{
                    width: "150px",
                    color: getComparisonColor(
                      summary.origSubtotal,
                      summary.updatedSubtotal,
                    ),
                  }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.updatedSubtotal)}
                </span>
              </div>
            </div>

            {/* Discount */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>Discount ({discountValue}%)</span>
              <div className="d-flex align-items-center">
                <span
                  className="text-end text-danger"
                  style={{ width: "150px" }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.origDiscountAmount)}
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start text-danger"
                  style={{ width: "150px" }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.updatedDiscountAmount)}
                </span>
              </div>
            </div>

            {/* Gross Amount */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>Gross Amount</span>
              <div className="d-flex align-items-center">
                <span className="text-end" style={{ width: "150px" }}>
                  {summary.symbol}
                  {formatToTwoDecimal(summary.origGrossAmount)}
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start"
                  style={{
                    width: "150px",
                    color: getComparisonColor(
                      summary.origGrossAmount,
                      summary.updatedGrossAmount,
                    ),
                  }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.updatedGrossAmount)}
                </span>
              </div>
            </div>

            <hr className="flex-grow-1 mx-3" />

            {/* VAT (green) */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span style={{ color: "#198754" }}>
                {parseNumber(watchedVat) > 0 && !isSpecialTax
                  ? `VAT (${parseNumber(watchedVat)}%)`
                  : "Non-VAT"}
              </span>
              <div className="d-flex align-items-center">
                <span
                  className="text-end"
                  style={{ width: "150px", color: "#198754" }}
                >
                  {parseNumber(watchedVat) > 0 && !isSpecialTax
                    ? `${summary.symbol}${formatToTwoDecimal(summary.origVatAmount)}`
                    : "0.00"}
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start"
                  style={{ width: "150px", color: "#198754" }}
                >
                  {parseNumber(watchedVat) > 0 && !isSpecialTax
                    ? `${summary.symbol}${formatToTwoDecimal(summary.updatedVatAmount)}`
                    : "0.00"}
                </span>
              </div>
            </div>

            {/* Withholding Tax */}
            {parseNumber(watchedTaxRate) > 0 && !isSpecialTax && (
              <div className="w-100 d-flex flex-row justify-content-between p-2">
                <span>Withholding Tax ({parseNumber(watchedTaxRate)}%)</span>
                <div className="d-flex align-items-center">
                  <span
                    className="text-end text-danger"
                    style={{ width: "150px" }}
                  >
                    ({summary.symbol}
                    {formatToTwoDecimal(summary.origTaxAmount)}
                    )
                  </span>
                  <div className="text-center mx-2" style={{ width: "30px" }}>
                    <i className="fa-solid fa-arrow-right" />
                  </div>
                  <span
                    className="text-start text-danger"
                    style={{ width: "150px" }}
                  >
                    ({summary.symbol}
                    {formatToTwoDecimal(summary.updatedTaxAmount)}
                    )
                  </span>
                </div>
              </div>
            )}

            {/* Special Tax Checkbox */}
            <div className="w-100 d-flex flex-row justify-content-between p-2 align-items-center">
              <Form.Check
                type="checkbox"
                id="special-tax-computation"
                label={
                  <span
                    title={`E = Gross Amount * 1.12\nL = Gross Amount * 0.12\nI = E * 0.01\nTax (Special Computation) = L - I`}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    Tax{" "}
                    <small className="text-muted">(Special Computation)</small>
                  </span>
                }
                className="mb-0 d-flex align-items-center gap-2"
                style={{ cursor: "pointer" }}
                disabled={!isEditable}
                {...register("isSpecialTax", {
                  onChange: (e) => {
                    const isChecked = e.target.checked;
                    setHasChanges(true);

                    if (isChecked) {
                      setBackupVat(watchedVat);
                      setBackupTaxId(watchedTaxId);
                      setBackupTaxRate(watchedTaxRate);

                      setValue("vat", "");
                      setValue("taxId", "");
                      setValue("taxRate", 0, { shouldDirty: true });
                    } else {
                      setValue("vat", backupVat);
                      setValue("taxId", backupTaxId);
                      setValue("taxRate", backupTaxRate, { shouldDirty: true });
                    }
                  },
                })}
              />
              {watchedIsSpecialTax && (
                <div className="d-flex align-items-center">
                  <span
                    className="text-end text-danger"
                    style={{ width: "150px" }}
                  >
                    {originalIsSpecialTax ? (
                      <>
                        {summary.symbol}
                        {formatToTwoDecimal(summary.origSpecialTax)}
                      </>
                    ) : (
                      "—"
                    )}
                  </span>
                  <div className="text-center mx-2" style={{ width: "30px" }}>
                    <i className="fa-solid fa-arrow-right" />
                  </div>
                  <span
                    className="text-start text-danger"
                    style={{ width: "150px" }}
                  >
                    {summary.symbol}
                    {formatToTwoDecimal(summary.updatedSpecialTax)}
                  </span>
                </div>
              )}
            </div>

            <hr className="flex-grow-1 mx-3" />

            {/* Weighing Fee (Non-Taxable) */}
            <div className="w-100 d-flex flex-row justify-content-between p-2">
              <span>
                Weighing Fee <small className="text-muted">(Non-Taxable)</small>
              </span>
              <div className="d-flex align-items-center">
                <span
                  className="text-end text-danger"
                  style={{ width: "150px" }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.wFee)}
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start text-danger"
                  style={{ width: "150px" }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.wFee)}
                </span>
              </div>
            </div>

            {/* Other Fees (Non-Taxable) header + add button */}
            <div className="w-100 d-flex flex-row justify-content-between p-2 mt-2">
              <span className="fw-semibold">
                Other Fees <small className="text-muted">(Non-Taxable)</small>
              </span>
              {isEditable && (
                <button
                  type="button"
                  onClick={() => {
                    appendOtherFee({ feeName: "", feeAmount: "" });
                    setHasChanges(true);
                  }}
                  className="border-0 text-primary bg-white p-0 fw-bold"
                >
                  + Add Fee
                </button>
              )}
            </div>

            {/* Original fees (read-only display) */}
            {watchedOriginalFees?.length > 0 && (
              <div className="w-100 px-2 pb-2 mb-2">
                <span
                  className="text-muted fst-italic"
                  style={{ fontSize: "0.85rem" }}
                >
                  Original Listed Fees:
                </span>
                {watchedOriginalFees.map((fee, index) => (
                  <div
                    key={index}
                    className="w-100 d-flex flex-row justify-content-between ps-3 pe-2 pt-1"
                  >
                    <span className="text-secondary small">
                      - {fee.feeName}
                    </span>
                    <span className="text-secondary small">
                      {summary.symbol}
                      {fee.feeAmount}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* New editable fee inputs */}
            {otherFeeFields.map((fee, index) => (
              <div
                key={fee.id}
                className="w-100 d-flex flex-row align-items-start justify-content-between p-2 gap-2"
              >
                <div className="flex-grow-1">
                  <Form.Control
                    {...register(`otherFeesNew.${index}.feeName`, {
                      required: "Fee Name is Required",
                    })}
                    isInvalid={!!errors?.otherFeesNew?.[index]?.feeName}
                    placeholder="Fee Name"
                    size="sm"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors?.otherFeesNew?.[index]?.feeName?.message}
                  </Form.Control.Feedback>
                </div>
                <div className="flex-grow-1">
                  <div className="input-group input-group-sm has-validation">
                    <span className="input-group-text">{summary.symbol}</span>
                    <Form.Control
                      {...register(`otherFeesNew.${index}.feeAmount`, {
                        onChange: formatInputNumber,
                        required: "Fee Amount is Required",
                      })}
                      isInvalid={!!errors?.otherFeesNew?.[index]?.feeAmount}
                      className="text-danger text-end"
                      placeholder="0.00"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors?.otherFeesNew?.[index]?.feeAmount?.message}
                    </Form.Control.Feedback>
                  </div>
                </div>
                {isEditable && (
                  <button
                    type="button"
                    onClick={() => removeOtherFee(index)}
                    className="btn btn-sm btn-outline-danger"
                  >
                    <i className="fa-solid fa-trash" />
                  </button>
                )}
              </div>
            ))}

            {/* Total Other Fees comparison */}
            <div className="w-100 d-flex flex-row justify-content-between p-2 pb-3">
              <span>Total Other Fees</span>
              <div className="d-flex align-items-center">
                <span
                  className="text-end text-danger"
                  style={{ width: "150px" }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.origOtherFeesTotal)}
                </span>
                <div className="text-center mx-2" style={{ width: "30px" }}>
                  <i className="fa-solid fa-arrow-right" />
                </div>
                <span
                  className="text-start text-danger"
                  style={{
                    width: "150px",
                    color: getComparisonColor(
                      summary.origOtherFeesTotal,
                      summary.updatedOtherFeesTotal,
                    ),
                  }}
                >
                  {summary.symbol}
                  {formatToTwoDecimal(summary.updatedOtherFeesTotal)}
                </span>
              </div>
            </div>

            <hr className="flex-grow-1 mx-3" />

            {/* Total Amount */}
            <Card className="bg-info-subtle border-info mt-2">
              <CardBody>
                <div className="w-100 d-flex flex-row justify-content-between p-2">
                  <span className="fw-bold">Total Amount:</span>
                  <span style={{ color: "blue", fontWeight: "bold" }}>
                    {summary.symbol}
                    {formatToTwoDecimal(summary.updatedFinalTotal)}
                  </span>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        <div className="row my-3 mx-1">
          <Card className="bg-body-tertiary">
            <CardBody>
              <div className="w-100 d-flex flex-row justify-content-between p-2">
                <span className="fw-semibold fs-5">Summary of Changes</span>
              </div>
              <div className="col-sm">
                <div className="w-100 d-flex flex-row justify-content-between p-2">
                  <span>Original Total Amount:</span>
                  <span style={{ color: "blue", fontWeight: "bold" }}>
                    {summary.symbol}
                    {formatToTwoDecimal(summary.origFinalTotal)}
                  </span>
                </div>
              </div>
              <div className="col-sm">
                <div className="w-100 d-flex flex-row justify-content-between p-2">
                  <span>New Total Amount:</span>
                  <span style={{ color: "orange", fontWeight: "bold" }}>
                    {summary.symbol}
                    {formatToTwoDecimal(summary.updatedFinalTotal)}
                  </span>
                </div>

                <hr className="flex-grow-1 mx-3" />
                <div className="w-100 d-flex flex-row justify-content-between p-2">
                  <span className="fw-bold">Difference:</span>
                  <span
                    style={{
                      color: summary.difference < 0 ? "red" : "green",
                      fontWeight: "bold",
                    }}
                  >
                    {summary.symbol}
                    {formatToTwoDecimal(summary.difference)}
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="row">
          <div className="w-100 d-flex flex-row justify-content-end p-2 gap-3">
            {/* ── CREATE MODE ── */}
            {!isViewMode && (
              <>
                <Button
                  variant="btn btn-secondary"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  form="re-open-ticket-form"
                  disabled={!hasChanges}
                >
                  Save Changes
                </Button>
              </>
            )}

            {/* ── VIEW MODE (not editing) ── */}
            {isViewMode && !isEditMode && (
              <>
                <Button
                  variant="btn btn-secondary"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>

                {/* Edit button — only show when Pending */}
                {status === "Pending" && (
                  <Button
                    variant="btn btn-primary"
                    type="button"
                    onClick={() => {
                      setIsEditMode(true);
                      setHasChanges(false);
                    }}
                  >
                    Edit
                  </Button>
                )}

                {/* Approved/Rejected badge */}
                {status === "Approved" && (
                  <Button
                    variant="success"
                    disabled
                    style={{ opacity: 1, cursor: "default" }}
                  >
                    <i className="fa-solid fa-check me-2" /> Approved
                  </Button>
                )}
                {status === "Rejected" && (
                  <Button
                    variant="danger"
                    disabled
                    style={{ opacity: 1, cursor: "default" }}
                  >
                    <i className="fa-solid fa-xmark me-2" /> Rejected
                  </Button>
                )}
              </>
            )}

            {/* ── EDIT MODE (view + editing) ── */}
            {isViewMode && isEditMode && (
              <>
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => {
                    setIsEditMode(false);
                    setHasChanges(false);
                    fetchData(id); // reset to original fetched data
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  form="re-open-ticket-form"
                  disabled={!hasChanges}
                >
                  Save Changes
                </Button>
              </>
            )}
          </div>
        </div>

        <Modal show={print} size="xl" onHide={() => setPrint(false)}>
          <div className="position-relative">
            <a
              href={payablePdf.url || undefined}
              download={`Payable - ${transaction_id}.pdf`}
            >
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip
                    id="tooltip-right"
                    className="me-2"
                    style={{ fontSize: "0.8rem" }}
                  >
                    Download as{" "}
                    <strong className="text-danger">
                      "Payable - {transaction_id}.pdf"
                    </strong>
                  </Tooltip>
                }
                delay={300}
              >
                <Button
                  variant="light"
                  className="position-absolute btn btn-light border border-secondary-subtle mb-5 rounded-5"
                  style={{ bottom: "-1.8rem", left: "1rem" }}
                  disabled={!payablePdf.url}
                >
                  <i className="fa-solid fa-download"></i>
                </Button>
              </OverlayTrigger>
            </a>

            {/* Approve and Reject inside PDF Modal */}
            <div
              className="position-absolute"
              style={{ bottom: "1.5rem", right: "2.5rem" }}
            >
              {status === "Pending" ? (
                <div className="d-flex gap-1">
                  <button
                    className="btn btn-sm btn-danger px-3"
                    type="button"
                    onClick={handleReject}
                  >
                    Reject
                  </button>
                  <button
                    className="btn btn-sm btn-success"
                    type="button"
                    onClick={handleApprove}
                  >
                    Approve
                  </button>
                </div>
              ) : (
                <span
                  className={`badge px-3 py-2 fs-6 ${
                    status === "Approved"
                      ? "bg-success-subtle text-success border border-success-subtle"
                      : "bg-danger-subtle text-danger border border-danger-subtle"
                  }`}
                  style={{ pointerEvents: "none" }}
                >
                  {status}
                </span>
              )}
            </div>

            <PayablePDFFrame url={payablePdf.url} error={payablePdf.error} />
          </div>
        </Modal>
      </Form>
    </div>
  );
};

export default PayableReOpenTicket;
